// File: src/store/slices/userSlice.js
// Purpose: Redux Toolkit slice for user state management
// Last modified: 2025-11-06

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';

// ==================== Async Thunks ====================

/**
 * Fetch current user
 */
export const fetchUser = createAsyncThunk(
    'user/fetchCurrent',
    async (_, { getState, rejectWithValue }) => {
        try {
            const { user } = getState().user;

            if (user && user.email) {
                return user;
            }

            const data = await apiService.auth.getCurrentUser();

            return data;
        } catch (error) {
            console.error('[fetchUser] Error:', error);
            return {};
        }
    },
    {
        condition: (_, { getState }) => {
            const { user, loading } = getState().user;
            if (user && user.email) return false;
            if (loading) {
                console.log('[fetchUser] Already loading, skipping');
                return false;
            }
            return true;
        },
    }
);

/**
 * Request login token via email
 */
export const requestLoginToken = createAsyncThunk(
    'user/requestLoginToken',
    async (email, { rejectWithValue }) => {
        try {
            await apiService.auth.requestLoginToken(email);
            return { email, sent: true };
        } catch (error) {
            console.error('[requestLoginToken] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

/**
 * Logout user
 */
export const logout = createAsyncThunk(
    'user/logout',
    async (_, { rejectWithValue }) => {
        try {
            await apiService.auth.logout();
            return null;
        } catch (error) {
            console.error('[logout] Error:', error);
            return rejectWithValue(error.message);
        }
    }
);

// ==================== Slice ====================

const initialState = {
    user: {},
    loading: false,
    error: null,
    sorting: 'name_a_z',
    filter: 'all',
    loginTokenSent: false,
    loginTokenEmail: null,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        updateSorting: (state, action) => {
            state.sorting = action.payload;
        },
        updateFilter: (state, action) => {
            state.filter = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetLoginToken: (state) => {
            state.loginTokenSent = false;
            state.loginTokenEmail = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch user
            .addCase(fetchUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
            })
            .addCase(fetchUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.user = {};
            })
            // Request login token
            .addCase(requestLoginToken.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestLoginToken.fulfilled, (state, action) => {
                state.loading = false;
                state.loginTokenSent = true;
                state.loginTokenEmail = action.payload.email;
            })
            .addCase(requestLoginToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Logout
            .addCase(logout.pending, (state) => {
                state.loading = true;
            })
            .addCase(logout.fulfilled, (state) => {
                state.loading = false;
                state.user = {};
            })
            .addCase(logout.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// ==================== Exports ====================

export const {
    updateSorting,
    updateFilter,
    clearError,
    resetLoginToken
} = userSlice.actions;

// Selectors
export const selectUser = (state) => state.user.user;
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;
export const selectSorting = (state) => state.user.sorting;
export const selectFilter = (state) => state.user.filter;
export const selectLoginTokenSent = (state) => state.user.loginTokenSent;
export const selectLoginTokenEmail = (state) => state.user.loginTokenEmail;

// Helper selectors
export const selectIsLoggedIn = (state) => !!state.user.user?.email;
export const selectIsAdmin = (state) => !!state.user.user?.isAdmin;

export default userSlice.reducer;
